import React, { useEffect, useRef, useState } from "react";

export default function LocalPreview({
  onStreamChange,
  tileAspect = "4 / 3",
  defaultMirror = true,
  onToggleChat, // 👈 chat-toggle meegegeven vanuit buiten
  onMicChange,  // 👈 mic-status doorgeven
  onCamChange,

  // ✅ unread + peek
  unreadCount = 0,
  forceShowUiKey = 0,
    shouldStopOnUnmount = false,

}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [mirrored] = useState(defaultMirror);

  const [showUi, setShowUi] = useState(true);

  // Helper: veilig video attachen + proberen te playen
  function attachVideo(el, stream) {
    if (!el) return;
    try {
      if (el.srcObject !== stream) el.srcObject = stream || null;
      if (stream) {
        const play = () => el.play().catch(() => {});
        if (el.readyState >= 2) play();
        else el.onloadedmetadata = play;
      }
    } catch {}
  }

  // ✅ getUserMedia EXACT 1x + cleanup (geen re-acquire bij cam toggle)
  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const constraints = {
          video: true, // altijd aanvragen; camOn regelen we via track.enabled
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        };

        const s = await navigator.mediaDevices.getUserMedia(constraints);

        if (!active) {
          try { s.getTracks().forEach((t) => t.stop()); } catch {}
          return;
        }

        streamRef.current = s;

        // initial toggles toepassen
        try {
          s.getAudioTracks().forEach((t) => (t.enabled = micOn));
          s.getVideoTracks().forEach((t) => (t.enabled = camOn));
        } catch {}

        // Parent informeren (App.js / PeerManager)
        try { onStreamChange?.(s); } catch {}

        // Preview attach
        attachVideo(videoRef.current, s);
      } catch (e) {
        console.warn("[LocalPreview] getUserMedia failed:", e);
        try { onStreamChange?.(null); } catch {}
      }
    })();

    return () => {
      active = false;

      const s = streamRef.current;
      streamRef.current = null;

    // Stop tracks alleen als parent dat expliciet wil (bijv. bij leave/logout)
// Anders kan een accidental unmount je mic "ended" maken.
if (shouldStopOnUnmount && s) {
  try { s.getTracks().forEach((t) => t.stop()); } catch {}
}


      attachVideo(videoRef.current, null);

      try { onStreamChange?.(null); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ 1x

  // ✅ Mic toggle (enable/disable tracks)
  useEffect(() => {
    const s = streamRef.current;
    if (!s) return;
    try {
      s.getAudioTracks().forEach((t) => (t.enabled = micOn));
    } catch {}
  }, [micOn]);

  // ✅ Cam toggle (enable/disable tracks) — géén nieuwe getUserMedia
  useEffect(() => {
    const s = streamRef.current;
    if (!s) return;
    try {
      s.getVideoTracks().forEach((t) => (t.enabled = camOn));
    } catch {}
  }, [camOn]);

  // auto-hide UI in tile
  useEffect(() => {
    if (!showUi) return;
    const id = setTimeout(() => setShowUi(false), 2200);
    return () => clearTimeout(id);
  }, [showUi]);

  // ✅ Force UI even tonen bij nieuw chatbericht (peek)
  useEffect(() => {
    if (!forceShowUiKey) return;
    setShowUi(true);
  }, [forceShowUiKey]);

  const wrapperStyle = {
    position: "relative",
    background: "#000",
    borderRight: "1px solid #020617",
    borderBottom: "1px solid #020617",
    height: "100%",
    width: "100%",
    overflow: "hidden",
    aspectRatio: tileAspect,
  };

  const innerStyle = {
    position: "absolute",
    inset: 0,
    background: "#000",
  };

  const videoStyle = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: mirrored ? "scaleX(-1)" : "none",
    display: "block",
    background: "#000",
  };

  const btnCircle = {
    width: 30,
    height: 30,
    borderRadius: 999,
    border: "none",
    background: "rgba(15,23,42,0.78)",
    color: "#e5e7eb",
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    fontSize: 16,
    boxShadow: "0 8px 20px rgba(0,0,0,0.6)",
  };

  return (
    <div
      style={wrapperStyle}
      onMouseMove={() => setShowUi(true)}
      onMouseEnter={() => setShowUi(true)}
    >
      <div style={innerStyle}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          disablePictureInPicture
          style={videoStyle}
          aria-label="Local preview"
        />

        {/* Optioneel (UX): overlay bij camera uit (track is disabled) */}
        {!camOn && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#bbb",
              fontSize: 14,
              background: "rgba(0,0,0,0.55)",
              userSelect: "none",
            }}
          >
            Camera uit
          </div>
        )}

        {/* Mic / Cam knoppen, auto-hide, linksonder */}
        <div
          style={{
            position: "absolute",
            left: 8,
            bottom: 8,
            display: showUi ? "flex" : "none",
            gap: 8,
          }}
        >
          {/* Mic-knop */}
          <button
            type="button"
            style={btnCircle}
            onClick={() =>
              setMicOn((m) => {
                const next = !m;
                onMicChange?.(next); // 👈 App.js laten weten
                return next;
              })
            }
            title={micOn ? "Microfoon uit" : "Microfoon aan"}
          >
            <svg
              viewBox="0 0 24 24"
              style={{ width: 18, height: 18 }}
              aria-hidden="true"
            >
              <g fill="currentColor">
                <path d="M12 4a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V7a3 3 0 0 0-3-3z" />
                <path d="M7 11a1 1 0 0 0-2 0 7 7 0 0 0 6 6.92V20H9a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2h-2v-2.08A7 7 0 0 0 19 11a1 1 0 1 0-2 0 5 5 0 0 1-10 0z" />
              </g>

              {!micOn && (
                <path
                  d="M5 5 L19 19"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  fill="none"
                />
              )}
            </svg>
          </button>

          {/* Cam-knop */}
          <button
            type="button"
            style={btnCircle}
           onClick={() =>
              setCamOn((m) => {
                const next = !m;
                onCamChange?.(next); // 👈 App.js laten weten
                return next;
              })
            }


            title={camOn ? "Camera uit" : "Camera aan"}
          >
            <svg
              viewBox="0 0 24 24"
              style={{ width: 18, height: 18 }}
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                d="M5 7a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7z"
              />
              <path fill="currentColor" d="M17 9l3-2v10l-3-2V9z" />

              {!camOn && (
                <path
                  d="M4 4 L20 20"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  fill="none"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Chat-knop, auto-hide, rechtsonder */}
        {typeof onToggleChat === "function" && (
          <div
            style={{
              position: "absolute",
              right: 8,
              bottom: 8,
              display: showUi ? "flex" : "none",
            }}
          >
            <button
              type="button"
              style={{ ...btnCircle, position: "relative" }}
              onClick={onToggleChat}
              title="Chat openen"
            >
              <svg
                viewBox="0 0 24 24"
                style={{ width: 16, height: 16 }}
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M4 5a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H9.5L6 18.5V15H7a3 3 0 0 1-3-3V5zm3-1a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2v1.59L10.59 12H17a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H7z"
                />
              </svg>

              {/* 🔔 unread indicator */}
              {unreadCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: "#22c55e",
                    boxShadow: "0 0 0 2px rgba(2,6,23,0.9)",
                  }}
                />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
